// Teacher paper workspace: one list for every draft and published paper, with
// one creation flow for manual entry, PDF extraction, or the teacher's own
// material ("From your content").
//
// The list is one academic year's — the header's switcher picks which. A
// previous year is read-only: its papers open, print and download, and the
// page offers nothing that would write (the server refuses it anyway).
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, uploadToS3 } from '@/lib/api';
import type {
  ConceptOption,
  ContentCandidate,
  ContentIngestMode,
  ContentIngestResult,
  CustomPaperStatus,
  TeacherClassroomDTO,
  TeacherPaperListItem,
} from '@/types';
import { CustomPaperPanel } from '@/components/teacher/CustomPaperPanel';
import { OriginalPdfButton } from '@/components/teacher/OriginalPdfButton';
import { PreviousYearNotice } from '@/components/teacher/TeacherYearSwitcher';
import { MathText } from '@/components/exam/MathText';
import { messageFor } from '@/components/teacher/paper/editorItems';
import { useTeacherYear, withYear } from '@/hooks/useTeacherYear';

/** "10 Aug 2026" — the date a teacher would write on the paper. */
const uploadDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const STATUS_LABEL: Record<CustomPaperStatus, string> = {
  draft: 'Draft',
  processing: 'Reading paper…',
  review: 'Ready to review',
  done: 'Ready to review',
  publishing: 'Publishing…',
  published: 'Published',
  failed: 'Failed',
};

const STATUS_CLASS: Record<CustomPaperStatus, string> = {
  draft: 'bg-slate-100 text-slate-700',
  processing: 'bg-amber-50 text-amber-700',
  review: 'bg-indigo-50 text-indigo-700',
  done: 'bg-indigo-50 text-indigo-700',
  publishing: 'bg-amber-50 text-amber-700',
  published: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
};

export function TeacherPapersPage() {
  const { paperId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isNew = location.pathname.endsWith('/new');
  const isEditor = Boolean(paperId) && location.pathname.endsWith('/edit');

  if (isNew) return <NewPaperForm onCancel={() => navigate('/teacher/papers')} />;
  if (isEditor && paperId) {
    return (
      <CustomPaperPanel
        selectedId={paperId}
        onSelect={() => undefined}
        standalone
        onBack={() => navigate('/teacher/papers')}
      />
    );
  }

  return <PaperList />;
}

function PaperList() {
  const navigate = useNavigate();
  const { yearParam, readOnly, year } = useTeacherYear();
  const [status, setStatus] = useState<'ALL' | CustomPaperStatus>('ALL');
  const [classroomId, setClassroomId] = useState('ALL');
  const papersQuery = useQuery({
    queryKey: ['teacher-papers', yearParam],
    queryFn: () => apiGet<TeacherPaperListItem[]>(withYear('/teacher/papers', yearParam)),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((paper) => paper.status === 'processing') ? 5_000 : false,
  });
  const classroomsQuery = useQuery({
    queryKey: ['teacher-classrooms', yearParam],
    queryFn: () => apiGet<TeacherClassroomDTO[]>(withYear('/teacher/classrooms', yearParam)),
  });

  const papers = (papersQuery.data ?? []).filter(
    (paper) =>
      (status === 'ALL' || paper.status === status) &&
      (classroomId === 'ALL' || paper.classroomId === classroomId),
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <h1 className="text-2xl font-bold text-forest">
            My papers{year ? <span className="text-ink-soft"> · {year.label}</span> : null}
          </h1>
          <p className="mt-1 text-sm text-ink-soft">
            {readOnly
              ? 'Last year’s papers, as they were set. Open one to print it, or download the PDF it was uploaded as.'
              : 'Create, review, publish and revisit every paper from one place.'}
          </p>
        </div>
        {!readOnly && (
          <button
            onClick={() => navigate('/teacher/papers/new')}
            className="rounded-xl bg-forest px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            + New paper
          </button>
        )}
      </div>

      <PreviousYearNotice />

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-line bg-card p-3">
        <span className="mr-1 text-xs font-semibold text-slate-500">Filter</span>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value as typeof status)}
          className="rounded-lg border border-line px-2.5 py-1.5 text-xs outline-none focus:border-forest"
        >
          <option value="ALL">All statuses</option>
          <option value="draft">Draft</option>
          <option value="processing">Reading paper</option>
          <option value="review">Ready to review</option>
          <option value="published">Published</option>
          <option value="failed">Failed</option>
        </select>
        <select
          value={classroomId}
          onChange={(event) => setClassroomId(event.target.value)}
          className="rounded-lg border border-line px-2.5 py-1.5 text-xs outline-none focus:border-forest"
        >
          <option value="ALL">All classrooms</option>
          {(classroomsQuery.data ?? []).map((classroom) => (
            <option key={classroom.id} value={classroom.id}>
              {classroom.name}
            </option>
          ))}
        </select>
        <span className="ml-auto text-xs text-slate-400">
          {papers.length} paper{papers.length === 1 ? '' : 's'}
        </span>
      </div>

      {papersQuery.isLoading ? (
        <div className="h-48 animate-pulse rounded-2xl bg-slate-100" />
      ) : papers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line bg-card py-16 text-center">
          <p className="text-sm font-semibold text-ink">No papers match these filters.</p>
          <p className="mt-1 text-xs text-slate-500">
            {readOnly
              ? 'You set no papers for these classes that year.'
              : 'Start with a blank manual paper or upload a PDF.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-line bg-card">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs font-semibold text-slate-500">
                <th className="px-4 py-3">Paper</th>
                <th className="px-4 py-3">Classroom</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3 text-center">Questions</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {papers.map((paper) => {
                const legacy = paper.id === paper.paperId;
                return (
                  <tr key={paper.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-ink">{paper.title}</p>
                      <p className="mt-0.5 text-fs-11 text-slate-400">
                        {paper.board} · Grade {paper.grade === 'TEN' ? '10' : '12'}
                      </p>
                      {/* Which file this was — the question "which paper did I
                          upload?" has no other answer on this screen. */}
                      {paper.source === 'UPLOAD' && paper.fileName && (
                        <p
                          className="mt-0.5 max-w-[280px] truncate text-fs-11 text-slate-500"
                          title={paper.fileName}
                        >
                          {paper.fileName}
                          {paper.uploadedAt && ` · uploaded ${uploadDate(paper.uploadedAt)}`}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{paper.classroomName ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{paper.subjectName ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {paper.source === 'UPLOAD' ? 'PDF upload' : 'Manual'}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-700">{paper.questionCount}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-fs-11 font-semibold ${STATUS_CLASS[paper.status]}`}
                      >
                        {STATUS_LABEL[paper.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2 text-xs font-semibold">
                        {!legacy && (
                          <button
                            onClick={() => navigate(`/teacher/papers/${paper.id}/edit`)}
                            className="text-forest hover:underline"
                          >
                            {paper.status === 'published' || paper.readOnly ? 'View paper' : 'Edit'}
                          </button>
                        )}
                        {paper.hasOriginal && (
                          <OriginalPdfButton customPaperId={paper.id} label="Original PDF" />
                        )}
                        {paper.paperId && (
                          <button
                            onClick={() =>
                              navigate(
                                `/teacher/analytics?paperId=${encodeURIComponent(paper.paperId!)}`,
                              )
                            }
                            className="text-slate-600 hover:text-forest hover:underline"
                          >
                            View analytics
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type Mode = 'MANUAL' | 'UPLOAD' | 'CONTENT';

const MODES: { value: Mode; title: string; blurb: string }[] = [
  {
    value: 'MANUAL',
    title: 'Enter manually',
    blurb: 'Start with a blank question editor and add rows as you go.',
  },
  {
    value: 'UPLOAD',
    title: 'Upload a question paper',
    blurb: 'AI reads a finished paper PDF — questions, answers and hints — into the editor.',
  },
  {
    value: 'CONTENT',
    title: 'From your content',
    blurb: 'Your notes or a chapter summary. Choose what to make of it, then tick what to keep.',
  },
];

const MAKE: { value: ContentIngestMode; title: string; blurb: string }[] = [
  {
    value: 'FROM_CONTENT',
    title: 'Questions from this content',
    blurb: 'New questions written from what the material says.',
  },
  { value: 'SAME', title: 'The same questions', blurb: 'The questions already in it, as written.' },
  {
    value: 'SIMILAR',
    title: 'Similar questions',
    blurb: 'Those questions again, with new numbers or a new example.',
  },
];

const card = (on: boolean) =>
  `rounded-xl border p-4 text-left ${on ? 'border-forest bg-forest/5 ring-1 ring-forest' : 'border-line hover:bg-paper-sunk'}`;
const field =
  'w-full rounded-xl border border-line bg-card px-3 py-2.5 text-sm outline-none focus:border-forest disabled:bg-paper-sunk';
const label = 'mb-1.5 block text-xs font-semibold text-slate-600';

/**
 * The new-paper form: the audience first, then how the questions arrive —
 * typed, read from a finished paper, or made from the teacher's own material.
 *
 * "From your content" takes pasted text only. A file would have to go up
 * presigned, and reading a chapter PDF is the paper extractor's job; pasting
 * the notes is the honest version of this today.
 */
function NewPaperForm({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [pickedRoom, setPickedRoom] = useState('');
  const [pickedSubject, setPickedSubject] = useState('');
  const [mode, setMode] = useState<Mode>('MANUAL');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');

  // From your content
  const [text, setText] = useState('');
  const [chapterId, setChapterId] = useState('');
  const [make, setMake] = useState<ContentIngestMode>('FROM_CONTENT');
  const [count, setCount] = useState(5);
  const [result, setResult] = useState<ContentIngestResult | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());

  // Always the current year: a paper is never set for last year's class,
  // whatever year the list behind this form was showing.
  const classroomsQuery = useQuery({
    queryKey: ['teacher-classrooms', ''],
    queryFn: () => apiGet<TeacherClassroomDTO[]>('/teacher/classrooms'),
  });
  const classrooms = useMemo(() => classroomsQuery.data ?? [], [classroomsQuery.data]);
  // The first room and its first subject until one is picked — derived, so no
  // effect has to set them.
  const classroom = classrooms.find((c) => c.id === pickedRoom) ?? classrooms[0] ?? null;
  const subjectId =
    classroom?.subjects.find((s) => s.id === pickedSubject)?.id ?? classroom?.subjects[0]?.id ?? '';

  const conceptsQuery = useQuery({
    queryKey: ['concepts', subjectId],
    enabled: mode === 'CONTENT' && Boolean(subjectId),
    queryFn: () => apiGet<ConceptOption[]>(`/teacher/concepts?subjectId=${subjectId}`),
  });
  const topics = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of conceptsQuery.data ?? []) seen.set(c.chapterId, c.chapterName);
    return [...seen].map(([id, name]) => ({ id, name }));
  }, [conceptsQuery.data]);

  const opened = (id: string) => {
    void queryClient.invalidateQueries({ queryKey: ['teacher-papers'] });
    void queryClient.invalidateQueries({ queryKey: ['custom-papers'] });
    navigate(`/teacher/papers/${id}/edit`);
  };

  const create = useMutation({
    mutationFn: async () => {
      const cleanTitle = title.trim();
      if (!cleanTitle) throw new Error('Give the paper a name.');
      if (!classroom || !subjectId) throw new Error('Choose a classroom and subject.');
      const audience = { title: cleanTitle, classroomId: classroom.id, subjectId };

      if (mode === 'MANUAL') {
        return (await apiPost<{ id: string }>('/teacher/custom-papers', audience)).id;
      }
      if (mode === 'CONTENT') {
        const items = (result?.candidates ?? [])
          .filter((c) => ticked.has(c.key))
          .map((c) => c.item);
        if (items.length === 0) throw new Error('Tick at least one question to keep.');
        return (await apiPost<{ id: string }>('/teacher/custom-papers', { ...audience, items })).id;
      }

      if (!file) throw new Error('Choose a PDF to upload.');
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        throw new Error('Only PDF files are supported.');
      }
      const { uploadUrl, s3Key } = await apiPost<{ uploadUrl: string; s3Key: string }>(
        '/uploads/paper-presign',
        { fileName: file.name, contentType: 'application/pdf', sizeBytes: file.size },
      );
      await uploadToS3(uploadUrl, file, 'application/pdf');
      return (
        await apiPost<{ customPaperId: string }>('/teacher/custom-upload', {
          ...audience,
          s3Key,
          fileName: file.name,
        })
      ).customPaperId;
    },
    onSuccess: opened,
    onError: (err: unknown) => setError(messageFor(err)),
  });

  const read = useMutation({
    mutationFn: () => {
      if (!subjectId) throw new Error('Choose a classroom and subject.');
      if (text.trim().length < 20)
        throw new Error('Paste the material first — a few sentences at least.');
      return apiPost<ContentIngestResult>('/teacher/content-ingest', {
        text,
        subjectId,
        chapterId: chapterId || undefined,
        mode: make,
        count,
      });
    },
    onSuccess: (r) => {
      setResult(r);
      // Everything starts ticked: the review is for taking out, which is the
      // quicker job when most of a reading is usable.
      setTicked(new Set(r.candidates.map((c) => c.key)));
      setError('');
    },
    onError: (err: unknown) => setError(messageFor(err)),
  });

  const toggle = (c: ContentCandidate) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(c.key)) next.delete(c.key);
      else next.add(c.key);
      return next;
    });

  /** Changing the material or the ask makes the last reading stale. */
  const stale = () => setResult(null);

  const continueLabel = create.isPending
    ? mode === 'UPLOAD'
      ? 'Uploading and reading…'
      : 'Creating…'
    : mode === 'CONTENT'
      ? `Create the paper with ${ticked.size} ticked`
      : 'Continue to editor';

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
      <div>
        <button
          onClick={onCancel}
          className="mb-2 text-xs font-semibold text-slate-500 hover:text-forest"
        >
          ← My papers
        </button>
        <h1 className="text-2xl font-bold text-forest">New paper</h1>
        <p className="mt-1 text-sm text-ink-soft">
          Choose the audience first, then how the questions arrive — typed, read from a paper, or
          made from your own material.
        </p>
      </div>

      <div className="rounded-2xl border border-line bg-card p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className={label}>Paper name</span>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="e.g. Algebra revision test"
              className={field}
            />
          </label>
          <label className="block">
            <span className={label}>Classroom</span>
            <select
              value={classroom?.id ?? ''}
              onChange={(event) => {
                setPickedRoom(event.target.value);
                setPickedSubject('');
                setChapterId('');
                stale();
              }}
              className={field}
            >
              {classrooms.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={label}>Subject</span>
            <select
              value={subjectId}
              onChange={(event) => {
                setPickedSubject(event.target.value);
                setChapterId('');
                stale();
              }}
              disabled={!classroom}
              className={field}
            >
              {(classroom?.subjects ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {/* A class teacher's own room, with no subject of theirs in it:
                they read its work, but its subject teachers set its papers. */}
            {classroom && classroom.subjects.length === 0 && (
              <span className="mt-1.5 block text-xs text-amber-700">
                {classroom.isClassTeacher
                  ? 'You look after this class but take no subject in it, so its subject teachers set its papers.'
                  : 'You take no subject in this class.'}
              </span>
            )}
          </label>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          {MODES.map((m) => (
            <button
              key={m.value}
              onClick={() => setMode(m.value)}
              className={card(mode === m.value)}
            >
              <p className="text-sm font-bold text-ink">{m.title}</p>
              <p className="mt-1 text-xs text-slate-500">{m.blurb}</p>
            </button>
          ))}
        </div>

        {mode === 'UPLOAD' && (
          <label className="mt-4 block rounded-xl border border-dashed border-line p-4">
            <span className={label}>Question paper PDF</span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              className="block w-full text-sm text-slate-600"
            />
            {file && (
              <span className="mt-2 block text-xs text-slate-500">Selected: {file.name}</span>
            )}
          </label>
        )}

        {mode === 'CONTENT' && (
          <div className="mt-5 flex flex-col gap-4">
            <label className="block">
              <span className={label}>The material — notes, a chapter summary, or a worksheet</span>
              <textarea
                value={text}
                onChange={(event) => {
                  setText(event.target.value);
                  stale();
                }}
                rows={8}
                maxLength={30_000}
                placeholder="Paste it here. Questions it already asks — numbered, or ending in ? — can be kept as they are; full sentences become things to ask about."
                className={`${field} resize-y`}
              />
            </label>

            <label className="block">
              <span className={label}>Which topic it covers (optional — narrows the tagging)</span>
              <select
                value={chapterId}
                onChange={(event) => {
                  setChapterId(event.target.value);
                  stale();
                }}
                className={field}
              >
                <option value="">Any topic in this subject</option>
                {topics.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>

            <div>
              <span className={label}>What to make of it</span>
              <div className="grid gap-2 sm:grid-cols-3">
                {MAKE.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => {
                      setMake(m.value);
                      stale();
                    }}
                    className={card(make === m.value)}
                  >
                    <p className="text-sm font-bold text-ink">{m.title}</p>
                    <p className="mt-1 text-xs text-slate-500">{m.blurb}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                Up to
                <select
                  value={count}
                  onChange={(event) => {
                    setCount(Number(event.target.value));
                    stale();
                  }}
                  className="rounded-lg border border-line bg-card px-2 py-1.5 text-xs"
                >
                  {[3, 5, 8, 10, 12].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                questions
              </label>
              <button
                onClick={() => read.mutate()}
                disabled={read.isPending}
                className="rounded-xl border border-forest px-4 py-2 text-sm font-semibold text-forest hover:bg-forest/5 disabled:opacity-50"
              >
                {read.isPending ? 'Reading…' : result ? 'Read it again' : 'Read it'}
              </button>
            </div>

            {result && (
              <div className="rounded-xl border border-line">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-paper-sunk px-4 py-2.5">
                  <p className="text-sm font-bold text-ink">
                    {result.candidates.length} found · {ticked.size} ticked
                  </p>
                  <p className="text-xs text-slate-500">Only the ticked ones go into the paper.</p>
                </div>
                {result.note && (
                  <p className="border-b border-line bg-amber-50 px-4 py-2 text-xs text-amber-800">
                    {result.note}
                  </p>
                )}
                <ul>
                  {result.candidates.map((c) => (
                    <li key={c.key} className="border-b border-line last:border-b-0">
                      <label className="flex cursor-pointer items-start gap-3 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={ticked.has(c.key)}
                          onChange={() => toggle(c)}
                          className="mt-1 h-4 w-4 flex-none"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-ink">
                            <MathText>{c.item.question}</MathText>
                          </span>
                          <span className="mt-0.5 block text-xs text-slate-500">
                            {c.item.marks} marks · {c.item.concept || 'untagged'}
                            {c.item.concept && !c.tagSure && (
                              <span className="font-semibold text-amber-700">
                                {' '}
                                (best guess — check it)
                              </span>
                            )}
                            {c.source && c.source !== c.item.question && (
                              <>
                                {' '}
                                · from: “
                                {c.source.length > 90 ? `${c.source.slice(0, 90)}…` : c.source}”
                              </>
                            )}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {error && (
          <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
            {error}
          </p>
        )}
        {classrooms.length === 0 && !classroomsQuery.isLoading && (
          <p className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
            You need a classroom assignment before creating a paper.
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-xl border border-line px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-paper-sunk"
          >
            Cancel
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={
              create.isPending ||
              classrooms.length === 0 ||
              !subjectId ||
              (mode === 'CONTENT' && (!result || ticked.size === 0))
            }
            className="rounded-xl bg-forest px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
